import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Check, AlertTriangle, Plus, ChevronRight } from 'lucide-react';
import ModalPortal from './ModalPortal';
import { colors } from '../theme/colors';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 800px;
  min-height: calc(100vh - 100px);
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ModalSubtitle = styled.div`
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem 2rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MatchItem = styled.div`
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  background: white;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #3b82f6;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MatchContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`;

const MatchText = styled.div`
  flex: 1;
`;

const ProvidedName = styled.div`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const MatchedName = styled.div`
  color: #6b7280;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ConfidenceScore = styled.div`
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  text-align: center;
  color: white;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  display: inline-block;
  margin-left: 0.375rem;
  vertical-align: middle;
  
  ${props => {
    if (props.confidence >= 0.95) {
      return `
        background: #0ea5e9;
      `;
    } else if (props.confidence >= 0.8) {
      return `
        background: #22c55e;
      `;
    } else if (props.confidence >= 0.6) {
      return `
        background: #eab308;
      `;
    } else {
      return `
        background: #ef4444;
      `;
    }
  }}
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.checked ? '#000000' : '#d1d5db'};
  border-radius: 4px;
  background: ${props => props.checked ? '#000000' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  cursor: pointer;
  margin-top: 0.125rem;
  flex-shrink: 0;
  
  &:hover {
    border-color: ${props => props.checked ? '#000000' : '#3b82f6'};
  }
`;

const NewFieldItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  background: ${props => props.selected ? '#fef3c7' : '#fffbeb'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.selected ? '#fde68a' : '#fef3c7'};
    border-color: #d97706;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const NewFieldText = styled.div`
  flex: 1;
`;

const NewFieldName = styled.div`
  font-weight: 600;
  color: #92400e;
  font-size: 0.875rem;
`;

const NewFieldNote = styled.div`
  color: #78716c;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const MatchActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? '#3b82f6' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#2563eb' : '#f3f4f6'};
    border-color: ${props => props.variant === 'primary' ? '#2563eb' : '#9ca3af'};
  }
  
  &.selected {
    background: #10b981;
    border-color: #10b981;
    color: white;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary ? `
    background: ${colors.primary};
    color: white;
    border: 1px solid ${colors.primary};
    
    &:hover {
      background: ${colors.primaryHover};
      border-color: ${colors.primaryHover};
    }
    
    &:disabled {
      background: #9ca3af;
      border-color: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

const FieldMatchModal = ({ 
  isOpen = false,
  matches = [],
  newFields = [],
  onClose = () => {},
  onConfirm = () => {}
}) => {
  const getInitialMatchState = () => 
    matches.reduce((acc, match, index) => {
      acc[index] = true; // Select all matches by default
      return acc;
    }, {});

  const getInitialNewFieldState = () =>
    newFields.reduce((acc, field, index) => {
      acc[index] = true; // Select new fields by default
      return acc;
    }, {});

  const [selectedMatches, setSelectedMatches] = useState(getInitialMatchState());
  const [selectedNewFields, setSelectedNewFields] = useState(getInitialNewFieldState());
  const [matchActions, setMatchActions] = useState({}); // Track action choice for each match

  // Reset state when modal opens or data changes
  useEffect(() => {
    if (isOpen) {
      setSelectedMatches(getInitialMatchState());
      setSelectedNewFields(getInitialNewFieldState());
      // Initialize match actions with default choices
      const initialActions = {};
      matches.forEach((match, index) => {
        initialActions[index] = match.suggested_action || 'enable_existing';
      });
      setMatchActions(initialActions);
    }
  }, [isOpen, matches, newFields]);

  const handleMatchToggle = (index) => {
    setSelectedMatches(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleNewFieldToggle = (index) => {
    setSelectedNewFields(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleActionSelect = (matchIndex, action) => {
    setMatchActions(prev => ({
      ...prev,
      [matchIndex]: action
    }));
  };

  const getMatchTypeLabel = (confidence) => {
    if (confidence >= 0.95) return 'Perfect Match';
    if (confidence >= 0.8) return 'Excellent Match';
    if (confidence >= 0.6) return 'Good Match';
    return 'Fair Match';
  };

  const resetSelections = () => {
    setSelectedMatches(getInitialMatchState());
    setSelectedNewFields(getInitialNewFieldState());
  };

  const handleClose = () => {
    resetSelections();
    onClose();
  };

  const handleConfirm = () => {
    const confirmedMatches = [];
    const confirmedNewFields = newFields.filter((_, index) => selectedNewFields[index]);
    
    // Process selected matches based on their action choice
    matches.forEach((match, index) => {
      if (selectedMatches[index]) {
        const action = matchActions[index] || 'enable_existing';
        
        if (action === 'enable_existing') {
          // Add to confirmed matches (existing behavior)
          confirmedMatches.push(match);
        } else if (action === 'create_new') {
          // Add to new fields instead
          confirmedNewFields.push({
            provided_name: match.provided_name,
            action: 'suggest_new',
            reason: 'User chose to create new instead of using existing match'
          });
        }
      }
    });
    
    onConfirm(confirmedMatches, confirmedNewFields);
  };

  const selectedCount = Object.values(selectedMatches).filter(Boolean).length + 
                       Object.values(selectedNewFields).filter(Boolean).length;

  if (!isOpen) return null;

  return (
    <ModalPortal isOpen={isOpen}>
      <ModalOverlay>
        <ModalContainer>
          <ModalHeader>
            <div>
              <ModalTitle>
                Field Match Results
              </ModalTitle>
              <ModalSubtitle>
                Review and confirm which fields to enable
              </ModalSubtitle>
            </div>
            <CloseButton onClick={handleClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          
          <ModalBody>
            {matches.length > 0 && (
              <Section>
                <SectionTitle>
                  <Check size={16} style={{ color: '#16a34a' }} />
                  Found Matches ({matches.length})
                </SectionTitle>
                {matches.map((match, index) => (
                  <MatchItem key={index}>
                    <MatchContent>
                      <Checkbox checked={selectedMatches[index]} onClick={() => handleMatchToggle(index)}>
                        {selectedMatches[index] && <Check size={12} color="white" />}
                      </Checkbox>
                      <MatchText>
                        <ProvidedName>
                          "{match.provided_name}"
                          <ConfidenceScore confidence={match.confidence}>
                            {getMatchTypeLabel(match.confidence)}
                          </ConfidenceScore>
                        </ProvidedName>
                        <MatchedName>
                          <ChevronRight size={12} style={{ display: 'inline', marginRight: '0.25rem' }} />
                          matches "{match.matched_field}"
                        </MatchedName>
                      </MatchText>
                    </MatchContent>
                    
                    {match.can_create_new && selectedMatches[index] && (
                      <MatchActions>
                        <ActionButton
                          variant={matchActions[index] === 'enable_existing' ? 'primary' : 'secondary'}
                          className={matchActions[index] === 'enable_existing' ? 'selected' : ''}
                          onClick={() => handleActionSelect(index, 'enable_existing')}
                        >
                          <Check size={14} />
                          Enable Existing
                        </ActionButton>
                        <ActionButton
                          variant={matchActions[index] === 'create_new' ? 'primary' : 'secondary'}
                          className={matchActions[index] === 'create_new' ? 'selected' : ''}
                          onClick={() => handleActionSelect(index, 'create_new')}
                        >
                          <Plus size={14} />
                          Create New
                        </ActionButton>
                      </MatchActions>
                    )}
                  </MatchItem>
                ))}
              </Section>
            )}

            {newFields.length > 0 && (
              <Section>
                <SectionTitle>
                  <Plus size={16} style={{ color: '#f59e0b' }} />
                  New Fields ({newFields.length})
                </SectionTitle>
                {newFields.map((field, index) => (
                  <NewFieldItem 
                    key={index}
                    selected={selectedNewFields[index]}
                    onClick={() => handleNewFieldToggle(index)}
                  >
                    <Checkbox checked={selectedNewFields[index]}>
                      {selectedNewFields[index] && <Check size={12} color="white" />}
                    </Checkbox>
                    <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
                    <NewFieldText>
                      <NewFieldName>"{field.provided_name}"</NewFieldName>
                      <NewFieldNote>No existing match found - will be added as new field</NewFieldNote>
                    </NewFieldText>
                  </NewFieldItem>
                ))}
              </Section>
            )}
          </ModalBody>
          
          <ModalFooter>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              primary 
              onClick={handleConfirm}
              disabled={selectedCount === 0}
            >
              Enable {selectedCount} Field{selectedCount !== 1 ? 's' : ''}
            </Button>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default FieldMatchModal;
